import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { MdFeedback } from "react-icons/md";
import { FiChevronDown, FiSettings, FiPrinter, FiSave } from "react-icons/fi";
import "./Unpaid.css";

const Unpaid = () => {
  const [showPayDropdown, setShowPayDropdown] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showCreateBillPopup, setShowCreateBillPopup] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [uploadedBills, setUploadedBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("All");
  const [billDateFilter, setBillDateFilter] = useState("All Dates");
  const [fromDate, setFromDate] = useState("-");
  const [toDate, setToDate] = useState("-");
  const [filteredBills, setFilteredBills] = useState([]);
  const [error, setError] = useState(null);

  const payDropdownRef = useRef(null);
  const addDropdownRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const uploadPopupRef = useRef(null);
  const filterPopupRef = useRef(null);
  const createPopupRef = useRef(null);

  // Memoize getSupplierName to prevent recreation
  const getSupplierName = useCallback((supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) return "Unknown Supplier";
    return supplier.supplier_display_name || `${supplier.first_name || ""} ${supplier.last_name || ""}`.trim();
  }, [suppliers]);

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/api/suppliers/", {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error(`Failed to fetch suppliers: ${res.status}`);
        const data = await res.json();
        setSuppliers(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        setError(`Failed to load suppliers: ${err.message}`);
      }
    };
    fetchSuppliers();
  }, []);

  // Fetch unpaid bills
  useEffect(() => {
    const fetchUnpaidBills = async () => {
      try {
        const response = await fetch("https://backend.hira-tech.co.uk/api/bills/unpaid/", {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error(`Failed to fetch unpaid bills: ${response.status}`);
        const data = await response.json();
        const formattedBills = data.map((bill) => {
          const today = new Date();
          const dueDate = new Date(bill.due_date);
          const daysOverdue = dueDate < today
            ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))
            : 0;
          const billAmount = parseFloat(bill.amount || bill.total || 0).toFixed(2);
          return {
            id: bill.id,
            supplier: getSupplierName(bill.supplier),
            date: new Date(bill.date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
            duedate: new Date(bill.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
            amount: `£${billAmount}`,
            openBalance: `£${billAmount}`,
            status: bill.status === "Pending" ? "Unpaid" : bill.status,
            daysOverdue: daysOverdue > 0 ? `${daysOverdue} days ago` : "-",
            action: bill.status === "Paid" ? "Show Payments" : "Mark as Paid",
            supplierId: bill.supplier, // Store supplierId for use in handleMarkAsPaid
            terms: bill.terms || "General", // Include terms for mapping
            memo: bill.memo || "",
            address: bill.address || "",
            bill_number: bill.bill_number || "",
            file_name: bill.bill_pdf ? bill.bill_pdf.split("/").pop() : "Manual Entry",
            file_path: bill.bill_pdf ? `https://backend.hira-tech.co.uk${bill.bill_pdf}` : "",
            items: bill.items || [], // Include items if available
          };
        });
        console.log("Formatted bills:", formattedBills);
        setUploadedBills(formattedBills);
        setFilteredBills(formattedBills);
        setSelectedBills(new Array(formattedBills.length).fill(false));
        setError(null);
      } catch (error) {
        console.error("Error fetching unpaid bills:", error);
        setError(`Failed to load unpaid bills: ${error.message}`);
      }
    };
    fetchUnpaidBills();
  }, [getSupplierName]);

  // Handle outside clicks
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (payDropdownRef.current && !payDropdownRef.current.contains(e.target)) {
        setShowPayDropdown(false);
      }
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
        setShowAddDropdown(false);
      }
      if (batchDropdownRef.current && !batchDropdownRef.current.contains(e.target) && !filterPopupRef.current?.contains(e.target)) {
        setShowBatchDropdown(false);
      }
      if (uploadPopupRef.current && !uploadPopupRef.current.contains(e.target)) {
        setShowUploadPopup(false);
      }
      if (createPopupRef.current && !createPopupRef.current.contains(e.target)) {
        setShowCreateBillPopup(false);
      }
      if (filterPopupRef.current && !filterPopupRef.current.contains(e.target)) {
        setShowFilterPopup(false);
      }
    };

    if (showPayDropdown || showAddDropdown || showBatchDropdown || showUploadPopup || showFilterPopup || showCreateBillPopup) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showPayDropdown, showAddDropdown, showBatchDropdown, showUploadPopup, showFilterPopup, showCreateBillPopup]);

  const handleUploadClick = (e) => {
    e.preventDefault();
    setShowUploadPopup(true);
  };

  const handleCreateClick = () => {
    setShowCreateBillPopup(true);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedBills(new Array(filteredBills.length).fill(newSelectAll));
  };

  const handleSelectBill = (index) => {
    const newSelectedBills = [...selectedBills];
    newSelectedBills[index] = !newSelectedBills[index];
    setSelectedBills(newSelectedBills);
    setSelectAll(newSelectedBills.every((selected) => selected));
  };

  const handleSupplierChange = (e) => {
    setSupplierFilter(e.target.value);
  };

  const handleBillDateChange = (e) => {
    setBillDateFilter(e.target.value);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const applyFilters = () => {
    let filtered = [...uploadedBills];

    if (supplierFilter !== "All") {
      filtered = filtered.filter((bill) => bill.supplier === supplierFilter);
    }

    const today = new Date();
    const getBillDate = (bill) => {
      const [day, month, year] = bill.date.split("/");
      return new Date(`${year}-${month}-${day}`);
    };

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstDayOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastQuarter = new Date(today.getFullYear(), Math.floor((today.getMonth() - 3) / 3) * 3, 1);
    const lastYear = new Date(today.getFullYear() - 1, 0, 1);

    switch (billDateFilter) {
      case "Since 365 Days ago":
        const oneYearAgo = new Date();
        oneYearAgo.setDate(today.getDate() - 365);
        filtered = filtered.filter((bill) => getBillDate(bill) >= oneYearAgo);
        break;
      case "This Month":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfMonth);
        break;
      case "This Quarter":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfQuarter);
        break;
      case "This Year":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfYear);
        break;
      case "Last Month":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return (
            billDate.getFullYear() === lastMonth.getFullYear() &&
            billDate.getMonth() === lastMonth.getMonth()
          );
        });
        break;
      case "Last Quarter":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return billDate >= lastQuarter && billDate < firstDayOfQuarter;
        });
        break;
      case "Last Year":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return billDate.getFullYear() === lastYear.getFullYear();
        });
        break;
      case "Custom":
        if (fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          filtered = filtered.filter((bill) => {
            const billDate = getBillDate(bill);
            return billDate >= from && billDate <= to;
          });
        }
        break;
      default:
        // "All dates" or anything not handled just means no filtering
    }

    console.log("Filtered bills:", filtered);
    setFilteredBills(filtered);
    setSelectedBills(new Array(filtered.length).fill(false));
    setSelectAll(false);
    setShowBatchDropdown(false);
  };

  const handleMarkAsPaid = async (id) => {
    const bill = filteredBills.find((b) => b.id === id);
    if (!bill) {
      alert("Cannot make payment: Invalid bill data.");
      return;
    }

    if (bill.status === "Paid") {
      alert("This bill has already been paid.");
      return;
    }

    try {
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/${id}/pay/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || "Failed to process payment. Please try again.";
        throw new Error(errorMessage);
      }

      const updatedBill = await response.json();
      console.log("Payment API response:", updatedBill);

      const billAmount = parseFloat(updatedBill.amount || updatedBill.total || 0).toFixed(2);
      const mappedBill = {
        id: updatedBill.id || id,
        type: "Bill",
        no: updatedBill.bill_number || bill.bill_number || "",
        payee: getSupplierName(updatedBill.supplier || bill.supplierId),
        category: updatedBill.terms ? updatedBill.terms : "General",
        terms: updatedBill.terms || bill.terms || "General",
        totalBeforeTax: billAmount ? (parseFloat(billAmount) / 1.17).toFixed(2) : "0.00",
        salesTax: billAmount ? (parseFloat(billAmount) * 0.17 / 1.17).toFixed(2) : "0.00",
        total: billAmount || "0.00",
        status: updatedBill.status || "Paid",
        address: updatedBill.address || bill.address || "",
        date: updatedBill.paid_date || updatedBill.date || bill.date,
        due_date: updatedBill.due_date || bill.due_date || "",
        memo: updatedBill.memo || bill.memo || "",
        supplier: updatedBill.supplier || bill.supplierId || "",
        bill_number: updatedBill.bill_number || bill.bill_number || "",
        file_name: updatedBill.bill_pdf
          ? updatedBill.bill_pdf.split("/").pop()
          : bill.file_name || "Manual Entry",
        file_path: updatedBill.bill_pdf
          ? `https://backend.hira-tech.co.uk${updatedBill.bill_pdf}`
          : bill.file_path || "",
        items: updatedBill.items
          ? updatedBill.items.map((item) => ({
              id: Date.now() + Math.random(),
              rowNumber: item.rowNumber || "1",
              category: item.category || updatedBill.terms || bill.terms || "General",
              description: item.description || "Initial Item",
              totalAmount: parseFloat(item.totalAmount || billAmount || 0).toFixed(2),
            }))
          : bill.items || [],
      };

      // Remove the paid bill from the table
      setUploadedBills((prev) => prev.filter((b) => b.id !== id));
      setFilteredBills((prev) => prev.filter((b) => b.id !== id));
      setSelectedBills((prev) => prev.filter((_, index) => filteredBills[index]?.id !== id));
      setSelectAll(false);
      setShowPaymentDetails(false);
      setSelectedBillId(null);

      alert("Payment processed successfully!");
    } catch (error) {
      console.error("Error processing payment:", error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div className="bills-container">
      {error && <div className="error-message" style={{ color: "red", marginBottom: "1rem" }}>{error}</div>}
      <h1>Expenses</h1>
      <nav className="nav-bar">
        <NavLink to="/admin/pk-expenses" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>
          Expenses
        </NavLink>
        <NavLink to="/admin/bills" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>
          Bills
        </NavLink>
        <NavLink to="/admin/pk-suppliers" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>
          Suppliers
        </NavLink>
      </nav>

      <div className="bills-header-row">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <p className="bills-title">Bills</p>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <NavLink to="/admin/add-bill" className="header-btn">
              <MdFeedback /> Give Feedback
            </NavLink>
            <div className="dropdowns-btn" onClick={() => setShowPayDropdown(!showPayDropdown)}>
              Pay bills <FiChevronDown />
              {showPayDropdown && (
                <div ref={payDropdownRef} className="dropdown-meu" style={{ width: "170px" }}>
                  <button>Make Recurring Bills</button>
                </div>
              )}
            </div>
            <div className="dropdown-button" onClick={() => setShowAddDropdown(!showAddDropdown)}>
              Add bill <FiChevronDown />
              {showAddDropdown && (
                <div ref={addDropdownRef} className="dropdown-meu" style={{ width: "200px", height: "120px" }}>
                  <button onClick={handleUploadClick}>Upload from computer</button>
                  <button onClick={handleCreateClick} disabled>
                    Create bill
                  </button>
                  <button disabled>Create recurring bill</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bills-top-bar">
        <div className="bills-status-filters">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <NavLink to="/admin/bills" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>
                For review
              </NavLink>
              <NavLink
                to="/admin/unpaid"
                className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}
                style={{ backgroundColor: "#d3d3d3" }}
              >
                Unpaid
              </NavLink>
              <NavLink to="/admin/paid" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>
                Paid
              </NavLink>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div
                className="dropdowns-btn filters"
                onClick={() => setShowBatchDropdown(!showBatchDropdown)}
                ref={batchDropdownRef}
              >
                <span className="batch-actions">Filters</span> <FiChevronDown />
                {showBatchDropdown && (
                  <div
                    ref={filterPopupRef}
                    className="dropdown-menu"
                    style={{ width: "400px", padding: "1rem" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <label>Supplier</label>
                      <select value={supplierFilter} onChange={handleSupplierChange}>
                        <option value="All">All</option>
                        {suppliers.map((supplier) => (
                          <option key={supplier.id} value={getSupplierName(supplier.id)}>
                            {getSupplierName(supplier.id)}
                          </option>
                        ))}
                      </select>
                      <label>Bill date</label>
                      <div className="date-container">
                        <select value={billDateFilter} onChange={handleBillDateChange}>
                          <option value="All Dates">All Dates</option>
                          <option value="Custom">Custom</option>
                          <option value="Since 365 Days ago">Since 365 Days ago</option>
                          <option value="This Month">This Month</option>
                          <option value="This Quarter">This Quarter</option>
                          <option value="This Year">This Year</option>
                          <option value="Last Month">Last Month</option>
                          <option value="Last Quarter">Last Quarter</option>
                          <option value="Last Year">Last Year</option>
                        </select>
                      </div>
                      {billDateFilter === "Custom" && (
                        <>
                          <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{ marginRight: "0.5rem" }}
                          />
                          <span style={{ marginRight: "0.5rem" }}>to</span>
                          <input type="date" value={toDate} onChange={handleToDateChange} />
                        </>
                      )}
                      <div
                        className="buttons"
                        style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem", gap: "1rem" }}
                      >
                        <button
                          className="reset-btn"
                          onClick={() => {
                            setSupplierFilter("All");
                            setBillDateFilter("All Dates");
                            setFromDate("");
                            setToDate("");
                            setFilteredBills(uploadedBills);
                          }}
                        >
                          Reset
                        </button>
                        <button className="apply-btn" onClick={applyFilters}>
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                className="action-filter"
                style={{
                  width: "200px",
                  fontSize: "15px",
                  borderRadius: "15px",
                  backgroundColor: "#ccc",
                  color: "black",
                  height: "35px",
                  marginTop: "15px",
                }}
              >
                Bill date: {billDateFilter === "Custom" ? "Custom" : billDateFilter}
              </button>
            </div>
          </div>
        </div>
        <div className="bills-actions">
          <div className="icon-actions">
            <button className="icon-action-btn" title="Settings">
              <FiSettings />
            </button>
            <button className="icon-action-btn" title="Print">
              <FiPrinter />
            </button>
            <button className="icon-action-btn" title="Save">
              <FiSave />
            </button>
          </div>
        </div>
      </div>

      <div className="billss-table">
        <div className="tables-row tables-header">
          <div className="checkbox-col">
            <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
          </div>
          <div className="supplier-col">SUPPLIER</div>
          <div className="bill-date-col">BILL DATE</div>
          <div className="due-date-col">DUE DATE</div>
          <div className="bill-amount-col">BILL AMOUNT</div>
          <div className="open-balance-col">OPEN BALANCE</div>
          <div className="status-col">STATUS</div>
          <div className="action-col">ACTION</div>
        </div>
        {filteredBills.length === 0 && (
          <div className="no-bills-message">
            <h3>No unpaid bills</h3>
          </div>
        )}
        {filteredBills.map((bill, index) => (
          <div key={bill.id} className="table-row">
            <div className="checkbox-col">
              <input type="checkbox" checked={selectedBills[index]} onChange={() => handleSelectBill(index)} />
            </div>
            <div className="supplier-col">{bill.supplier}</div>
            <div className="bill-date-col">{bill.date}</div>
            <div className="due-date-col">{bill.duedate}</div>
            <div className="bill-amount-col">{bill.amount}</div>
            <div className="open-balance-col">{bill.openBalance}</div>
            <div className="status-col">
              {bill.status} {bill.daysOverdue}
            </div>
            <div className="action-col">
              <button className="actionbtn mark-as-paid" onClick={() => handleMarkAsPaid(bill.id)}>
                {bill.action}
              </button>
              {bill.status === "Paid" && (
                <button
                  className="actionbtn"
                  onClick={() => {
                    setSelectedBillId(bill.id);
                    setShowPaymentDetails(bill.id === selectedBillId ? false : true);
                  }}
                >
                  {showPaymentDetails && selectedBillId === bill.id ? "Hide Details" : "Show Payments"}
                </button>
              )}
            </div>
          </div>
        ))}
        {showPaymentDetails && selectedBillId && (
          <div className="payment-details">
            <p>Payment details for bill ID: {selectedBillId}</p>
            <button className="actionbtn" onClick={() => setShowPaymentDetails(false)}>
              Hide Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unpaid;